import { apiClient } from './apiClient';
import type { BlastRadiusAssessment } from './googleAdsWeeklyService';

export interface PlacementReviewRow {
  campaign_id: string;
  campaign_name: string;
  campaign_type: 'PERFORMANCE_MAX' | 'DISPLAY' | 'DEMAND_GEN';
  placement: string;
  placement_type: string;
  display_name: string;
  target_url: string;
  metrics: Record<string, number>;
  already_excluded: boolean;
  mobile_app: boolean;
  kids_content: boolean;
  gaming: boolean;
  gambling: boolean;
  wrong_geo: boolean;
  wrong_language: boolean;
  reasoning: string;
  flagged: boolean;
}

export interface CampaignReadFailure {
  campaign_id: string;
  campaign_name: string;
  campaign_type: string;
}

export interface PlacementReviewResult {
  tenant_id: string;
  customer_id: string | null;
  placements: PlacementReviewRow[];
  campaigns_read: number;
  demand_gen_attempted: boolean;
  demand_gen_supported: boolean;
  // Campaigns whose placement read FAILED — distinct from a campaign read
  // successfully with zero placements.
  campaigns_read_failed: CampaignReadFailure[];
  // True when the account has more than one NEGATIVE_PLACEMENTS shared set
  // and no sharedSetResourceName was passed to disambiguate — every
  // already_excluded=false on a PMax/Demand Gen row may be an under-report
  // in that case, not confirmed "not excluded". Resubmit runPlacementReview
  // with the operator's picked sharedSetResourceName to resolve it.
  shared_set_ambiguous: boolean;
  generated_at: string | null;
  /**
   * CORE-P-02.c: false when GOOGLE_ADS_PLACEMENT_EXCLUSION_ENABLED is off
   * for the account. The read still succeeds; only POST /exclude refuses
   * (503). Optional so an older backend that omits it reads as enabled.
   */
  exclusion_write_enabled?: boolean;
}

/**
 * HTTP 409 ``detail`` payload emitted by the exclude route when the
 * blast-radius breaker demands typed confirmation. Same contract shape as
 * the Search Terms Negatives apply route (googleAdsWeeklyService.ts).
 */
export interface PlacementBlastRadiusConfirmationDetail {
  code: 'blast_radius_confirmation_required';
  assessment: BlastRadiusAssessment;
  confirmation_phrase: string;
}

export interface SharedSetCandidate {
  resource_name: string;
  name: string;
  member_count: number;
}

/**
 * HTTP 409 ``detail`` payload emitted when the account carries more than
 * one NEGATIVE_PLACEMENTS shared set and the backend can't guess which one
 * to write to.
 */
export interface PlacementAmbiguousSharedSetDetail {
  code: 'ambiguous_shared_set';
  candidates: SharedSetCandidate[];
  message: string;
}

export interface PlacementExclusionSelection {
  campaign_id: string;
  campaign_type: string;
  placement: string;
  placement_type: string;
}

export interface FailedPlacementItem {
  placement: string;
  reason: string;
}

export interface ExcludePlacementsResult {
  excluded: number;
  skipped: number;
  failed_placements: FailedPlacementItem[];
  attached_campaigns: number;
  mechanism_summary: Record<string, unknown>;
  blast_radius_required: boolean;
  // True when the write ran in dry-run mode — nothing was actually written
  // to Google Ads even though `excluded` is nonzero.
  dry_run: boolean;
  client_safe_summary: string;
}

export async function runPlacementReview(params: {
  tenantId: string;
  targetCountries?: string;
  targetLanguages?: string;
  sharedSetResourceName?: string;
}): Promise<PlacementReviewResult> {
  const { data } = await apiClient.get<PlacementReviewResult>(
    '/google-ads/placement-review/run',
    {
      params: {
        tenant_id: params.tenantId,
        target_countries: params.targetCountries || undefined,
        target_languages: params.targetLanguages || undefined,
        shared_set_resource_name: params.sharedSetResourceName || undefined,
      },
      // LLM classification over potentially hundreds of placements can
      // take a while.
      timeout: 120_000,
    },
  );
  return data;
}

export async function excludePlacements(params: {
  tenantId: string;
  selected: PlacementExclusionSelection[];
  confirmed: boolean;
  confirmationText?: string;
  sharedSetResourceName?: string;
}): Promise<ExcludePlacementsResult> {
  const { data } = await apiClient.post<ExcludePlacementsResult>(
    '/google-ads/placement-review/exclude',
    {
      tenant_id: params.tenantId,
      selected: params.selected,
      confirmed: params.confirmed,
      confirmation_text: params.confirmationText ?? '',
      shared_set_resource_name: params.sharedSetResourceName ?? undefined,
    },
  );
  return data;
}
